import { BlotterTable, Box, Input, isGridApiReady, useBlotterTable, type ColumnDef } from '@talos/kyoko';
import type { FirstDataRenderedEvent, SelectionChangedEvent } from 'ag-grid-enterprise';
import { useCallback, useMemo, useState } from 'react';
import { of } from 'rxjs';

interface SelectionBlotterProps<T extends object> {
  availableItems: T[];
  selectedItems: T[];
  onSelectionChanged: (selectedItems: T[]) => void;
  columns: ColumnDef<T>[];
  rowID: keyof T & string;
  searchPlaceholder: string;
}

export function SelectionBlotter<T extends object>({
  availableItems,
  selectedItems,
  onSelectionChanged,
  columns,
  rowID,
  searchPlaceholder,
}: SelectionBlotterProps<T>) {
  const [searchText, setSearchText] = useState('');

  const handleRowSelectionChanged = useCallback(
    ({ api }: SelectionChangedEvent<T>) => {
      if (!isGridApiReady(api)) {
        return;
      }
      onSelectionChanged(api.getSelectedRows());
    },
    [onSelectionChanged]
  );

  const handleFirstDataRendered = useCallback(
    ({ api }: FirstDataRenderedEvent<T>) => {
      const selectedIds = new Set(selectedItems.map(item => item[rowID]));
      api.forEachNode(node => {
        if (node.data && selectedIds.has(node.data[rowID])) {
          node.setSelected(true, false, 'api');
        }
      });
    },
    [selectedItems, rowID]
  );

  const dataObservable = useMemo(() => of({ data: availableItems }), [availableItems]);

  const blotterTable = useBlotterTable({
    gridOptions: {
      rowSelection: {
        mode: 'multiRow',
        checkboxes: true,
        headerCheckbox: true,
        copySelectedRows: true,
        enableClickSelection: true,
        enableSelectionWithoutKeys: true,
      },
      selectionColumnDef: { pinned: 'left', width: 32 },
      onSelectionChanged: handleRowSelectionChanged,
      onFirstDataRendered: handleFirstDataRendered,
    },
    dataObservable,
    rowID,
    columns,
    quickSearchParams: {
      filterText: searchText,
    },
    errorObservable: undefined,
  });

  return (
    <>
      <Box p="spacing8" borderBottom="solid 1px" borderColor="backgroundDivider">
        <Input
          placeholder={searchPlaceholder}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          data-testid="selection-blotter-search-input"
        />
      </Box>
      <BlotterTable {...blotterTable} background="backgroundModal" />
    </>
  );
}
